import type { MigrationContext } from '@sites/entity-migrator';

import { VERTICAL_ALIGN_BY_POSITION } from '../../../mixins/alignment';
import { cssKeywordOrNull } from '../../../utils';

import type { SectionDefaultV0 } from '../default/v0';
import type { SectionSpecificV0 } from '../specific/v0';
import type { SectionZeroV0 } from '../zero/v0';
import type { LegacyDefaultSection, LegacySpecificSection } from './entity';

/**
 * Legacy style values are `string | number` — the host writes paddings as
 * numbers but nothing stopped a `'30'` getting in, so parse rather than
 * type-check.
 */
const num = (value: unknown): number => {
    const parsed = Number(value);

    return Number.isFinite(parsed) ? parsed : 0;
};

const px = (value: unknown) => ({ value: num(value), unit: 'px' as const });

/**
 * The legacy bag keeps a theme preset name and a literal colour in the same
 * string, and so does v0 (`mixins/color-value`) — the host's `colorCss` tells
 * them apart against its own palette, which this package cannot see. So the
 * string travels verbatim.
 */
const color = (value: unknown): string | null => (value == null ? null : String(value));

const spacing = (styles: LegacySpecificSection['styles']) => ({
    value: {
        top: num(styles?.paddingTop),
        right: num(styles?.paddingRight),
        bottom: num(styles?.paddingBottom),
        left: num(styles?.paddingLeft),
    },
    unit: 'px' as const,
});

const envelope = (section: { id: string; blockId: string; columns: unknown[] }) => ({
    id: section.id,
    parentId: section.blockId,
    entityType: 'section' as const,
    children: section.columns,
    version: 0 as const,
    // Created before the field existed — when is not known.
    meta: { createdAt: null },
});

export const migrateLegacyDefaultToV0 = (input: LegacyDefaultSection): SectionDefaultV0 => ({
    ...envelope(input),
    entitySubType: 'default',
    props: {
        columnGap: px(input.columnIndent),
        isFlexibleWidths: Boolean(input.columnWidthFlex),
    },
});

/**
 * Sections of a zero block are indistinguishable from ordinary ones in the
 * persisted JSON — both carry `type: 'DEFAULT'`. The parent's type is the only
 * signal, so the adapter passes it down and the registry routes on it.
 */
export const migrateLegacyZeroToV0 = (input: LegacyDefaultSection): SectionZeroV0 => ({
    ...envelope(input),
    entitySubType: 'zero',
    props: {},
});

export const migrateLegacySpecificToV0 = (input: LegacySpecificSection): SectionSpecificV0 => {
    const { data, styles } = input;

    return {
        ...envelope(input),
        entitySubType: 'specific',
        props: {
            slotVerticalMargin: px(input.columnIndent),
            columnGap: px(styles?.columnGap),
            padding: spacing(styles),
            verticalPosition: VERTICAL_ALIGN_BY_POSITION[data?.verticalPosition ?? 'M'],
            slots: {
                left: data?.showLeftColumn ?? true,
                center: data?.showCenterColumn ?? true,
                right: data?.showRightColumn ?? true,
            },
            background: {
                color: color(styles?.backgroundColor),
                image: data?.backgroundImage ?? null,
                size: cssKeywordOrNull(styles?.backgroundSize),
                position: cssKeywordOrNull(styles?.backgroundPosition),
                repeat: cssKeywordOrNull(styles?.backgroundRepeat),
            },
        },
    };
};

/** Zero rows are routed by the parent block, which only the adapter knows. */
export const isZeroSectionContext = (ctx?: MigrationContext): boolean => ctx?.parentType === 'zero';
